/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './Transaction';
import { analyzeTransaction } from './TransactionAnalyze';

const TransactionManager: ComponentManagerFn<'Transaction'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'Transaction'>(platform, 'Transaction'),
    type: 'Transaction',
    name: 'Transaction',
    isChildAllowed: (page, item, childType, { appUid, pageId, itemId }) => {
      if (
        (
          ['TriggerBeforeSave', 'TriggerAfterSave', 'Transaction'] as ItemType[]
        ).includes(childType)
      ) {
        return true;
      }
      if (childType === 'TransactionMapping') {
        return !!platform.findParentOfType(
          appUid,
          pageId,
          itemId,
          'Transaction',
        );
      }
      return false;
    },
    defaultValue,
    props: {
      alias: {
        label: 'Alias',
        type: 'DataSourceAliasChooser',
        required: true,
        allowedTypes: ['ReadOnlyDataStore', 'DataStore'],
      },
      transactionCode: {
        label: 'Transaction Code',
        type: 'string',
        required: (item, { platform: _platform, appUid, pageId, itemId }) => {
          const parent = _platform.findParentOfType(
            appUid,
            pageId,
            itemId,
            'Transaction',
          );
          return !parent;
        },
      },
      autoQueryChildStores: {
        label: 'Auto Query Child Stores',
        type: 'boolean',
      },
    },
    isPropertyVisible(
      prop,
      itemData,
      { platform: _platform, appUid, pageId, itemId },
    ): boolean {
      const parent = _platform.findParentOfType(
        appUid,
        pageId,
        itemId,
        'Transaction',
      );
      switch (prop) {
        case 'transactionCode':
          return !parent;
        default:
          return true;
      }
    },
    onPropertyChange: (
      property,
      value,
      itemData,
      { appUid, pageId, itemId },
    ) => {
      if (property === 'alias') {
        let hasParent = true;
        let childId = itemId;
        while (hasParent) {
          const parent = platform.findParentOfType(
            appUid,
            pageId,
            childId,
            'Transaction',
          );
          if (!parent) {
            hasParent = false;
            break;
          }
          if (parent.props.alias === value) {
            platform.showError(
              `Alias ${value} already used by one of the parent transaction!`,
            );
            return { [property]: '' };
          }
          childId = parent.itemId;
        }
      }
      return {
        [property]: value,
      };
    },
    getDisplayText: (item) => item.props.alias,
    category: 'Data',
    icon: { iconName: 'database' },
    analyze: analyzeTransaction,
    description: `Similar to JDBC Transaction, this component can be used to group multiple datastores into a single transaction. When you post the changes using Save Transaction action, all the changes across all the datastores inside this transaction will be posted in a single request. If one datasource fails, then all changes within this transaction will be rolled back.
As a best practice always use Transaction when developing data entry applications. With transaction, you can define the parent-child relationship between datasources using the Mapping.

**Querying Child Stores**<br/>
When Auto Query Child Stores is checked, all the child datasources will be automatically queried when the user navigates between the parent store records. The child stores will be filtered using the parent attribute values of the defined Mapping.
    
**Defaulting of Child Attributes**<br/>
Let's take a simple example of Dept (Dept ID, Dept Name) & Emp (Emp ID, Emp Name & Emp Dept ID) datasources as parent child Transactions with a Mapping Dept ID - Emp Dept ID. Let's assume the Dept ID & Emp ID are auto generated on the server only after saving the transaction. When you create a new department and emp at the same time and save the transaction, the platform will first create the department which would generate a Dept ID for the newly created department and then copy the Dept ID from the Dept datasource record into Emp Dept ID attribute of Emp datasource record before saving the Emp datasource.`,
  };
};

export default TransactionManager;
